import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import { useTranslation } from "react-i18next";
import { updateTrainingSessionGrid } from "./funtionsBD/Mainservice";

const GridConfigDialog = ({
  open,
  onClose,
  selectedSession,
  initialColumns,
  initialRows,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [gridColumns, setGridColumns] = useState(1);
  const [gridRows, setGridRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      // Initialize with provided initial values or session values or defaults
      setGridColumns(initialColumns !== undefined ? initialColumns : (selectedSession?.ts_grid_columns || 1));
      setGridRows(initialRows !== undefined ? initialRows : (selectedSession?.ts_grid_rows || null));
      setError(null);
    }
  }, [open, selectedSession, initialColumns, initialRows]);

  const handleSave = async () => {
    // Validate inputs
    if (gridColumns < 1 || gridColumns > 20) {
      setError(t("grid_columns_invalid"));
      return;
    }
    if (gridRows !== null && (gridRows < 1 || gridRows > 20)) {
      setError(t("grid_rows_invalid"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await updateTrainingSessionGrid({
        ts_id: selectedSession.ts_id,
        ts_grid_columns: gridColumns,
        ts_grid_rows: gridRows || null,
      });

      if (onUpdate) {
        onUpdate(gridColumns, gridRows || null);
      }
      onClose();
    } catch (err) {
      console.error("Error updating grid configuration:", err);
      setError(t("error_saving_grid_config"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "12px",
          padding: "8px",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <SettingsIcon color="primary" />
            <Typography variant="h6" component="span">
              {t("configure_grid_layout")}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("grid_layout_description")}
          </Typography>

          <TextField
            fullWidth
            label={t("grid_columns")}
            type="number"
            value={gridColumns}
            onChange={(e) => setGridColumns(parseInt(e.target.value) || 1)}
            inputProps={{ min: 1, max: 20 }}
            sx={{ mb: 2 }}
            helperText={t("grid_columns_helper")}
          />

          <TextField
            fullWidth
            label={t("grid_rows")}
            type="number"
            value={gridRows || ""}
            onChange={(e) => setGridRows(e.target.value ? parseInt(e.target.value) : null)}
            inputProps={{ min: 1, max: 20 }}
            helperText={t("grid_rows_helper")}
          />

          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          <Box sx={{ mt: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
              {t("examples")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • {t("grid_example_1")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • {t("grid_example_2")}
            </Typography>
            <Typography variant="body2">
              • {t("grid_example_3")}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{ bgcolor: "#0877BC", "&:hover": { bgcolor: "#065a8a" } }}
        >
          {loading ? t("saving") : t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GridConfigDialog;

